import { kebabCase } from 'change-case';

import type { WorkspaceResourceKind } from '@/shared/components/workspace-resources';

import type { NotificationGroupConfig } from './components/NotificationGroup';

export type NotificationScope = 'nestedResource' | 'resource' | 'workspace';

interface NotificationGroupDefinition extends NotificationGroupConfig {
    resource: WorkspaceResourceKind;
    scope: NotificationScope;
}

const activityOption = (key: string, resourceLabel: string) => ({
    key,
    title: 'Activity',
    description: `Notify me about lifecycle and content changes to subscribed ${resourceLabel}`,
});

const commentOption = (key: string, resourceLabel: string) => ({
    key,
    title: 'Comments and Replies',
    description: `Notify me when someone comments on or replies within a subscribed ${resourceLabel}`,
});

const collaboratorOption = (key: string, resourceLabel: string) => ({
    key,
    title: 'New Collaborators',
    description: `Notify me when someone is added as a collaborator to a ${resourceLabel} I follow`,
});

export const buildNotificationGroups = (userFirstName?: string): NotificationGroupDefinition[] => {
    const handle = userFirstName ? kebabCase(userFirstName.toLocaleLowerCase()) : 'first-name';

    return [
        {
            key: 'workspace',
            resource: 'workspace',
            scope: 'workspace',
            title: 'Workspace',
            options: [
                {
                    key: 'workspace_member_added',
                    title: 'New Workspace Members',
                    description: 'Notify me when a member is added to a Workspace where I am an Admin or Owner',
                },
                {
                    key: 'workspace_alert_triggered',
                    title: 'Custom Alerts',
                    description: 'Notify me when a governed custom Alert matches records I can access',
                },
            ],
        },
        {
            key: 'folders',
            resource: 'folder',
            scope: 'resource',
            title: 'Folders',
            options: [
                activityOption('folder_activity', 'Folder'),
                collaboratorOption('folder_collaborator_added', 'Folder'),
            ],
        },
        {
            key: 'investments',
            resource: 'investment',
            scope: 'resource',
            title: 'Investments',
            options: [
                activityOption('investment_activity', 'Investment'),
                commentOption('investment_comment', 'Investment'),
                {
                    key: 'investment_mention',
                    title: 'Mentions',
                    description: `Notify me when someone uses my handle (e.g. @${handle}) in an Investment`,
                },
                collaboratorOption('investment_collaborator_added', 'Investment'),
            ],
        },
        {
            key: 'memos',
            resource: 'memo',
            scope: 'resource',
            title: 'Memos',
            options: [
                activityOption('memo_activity', 'Memo'),
                commentOption('memo_comment', 'Memo'),
                {
                    key: 'memo_mention',
                    title: 'Mentions',
                    description: `Notify me when someone uses my handle (e.g. @${handle}) in a Memo`,
                },
                collaboratorOption('memo_collaborator_added', 'Memo'),
            ],
        },
        {
            key: 'pipelines',
            resource: 'pipeline',
            scope: 'resource',
            title: 'Pipelines',
            options: [
                activityOption('pipeline_activity', 'Pipeline'),
                commentOption('pipeline_comment', 'Pipeline'),
                collaboratorOption('pipeline_collaborator_added', 'Pipeline'),
            ],
        },
        {
            key: 'tasks',
            resource: 'task',
            scope: 'resource',
            title: 'Tasks',
            options: [
                activityOption('task_activity', 'Task'),
                commentOption('task_comment', 'Task'),
                {
                    key: 'task_assigned',
                    title: 'Assignments',
                    description: 'Notify me when I am assigned responsibility for a Task',
                },
                {
                    key: 'task_review_requested',
                    title: 'Review Requests',
                    description: 'Notify me when I am asked to review a Task',
                },
                {
                    key: 'task_mention',
                    title: 'Mentions',
                    description: `Notify me when someone uses my handle (e.g. @${handle}) in a Task`,
                },
                {
                    key: 'task_due_soon',
                    title: 'Due Soon',
                    description: 'Notify me when a Task I own, am assigned, or review is due within 24 hours',
                },
                {
                    key: 'task_overdue',
                    title: 'Overdue',
                    description: 'Notify me when a Task I own, am assigned, or review becomes overdue',
                },
            ],
        },
        {
            key: 'dashboards',
            resource: 'dashboard',
            scope: 'resource',
            title: 'Dashboards',
            options: [
                activityOption('dashboard_activity', 'Dashboard'),
                commentOption('dashboard_comment', 'Dashboard'),
                {
                    key: 'dashboard_mention',
                    title: 'Mentions',
                    description: `Notify me when someone uses my handle (e.g. @${handle}) in a Dashboard`,
                },
                collaboratorOption('dashboard_collaborator_added', 'Dashboard'),
            ],
        },
        {
            key: 'reports',
            resource: 'report',
            scope: 'nestedResource',
            title: 'Reports',
            options: [activityOption('report_activity', 'Dashboard Report')],
        },
        {
            key: 'reportComponents',
            resource: 'reportComponent',
            scope: 'nestedResource',
            title: 'Report Components',
            options: [activityOption('report_component_activity', 'Report Component')],
        },
        {
            key: 'lists',
            resource: 'list',
            scope: 'resource',
            title: 'Lists',
            options: [
                activityOption('list_activity', 'List'),
                commentOption('list_comment', 'List'),
                {
                    key: 'list_mention',
                    title: 'Mentions',
                    description: `Notify me when someone uses my handle (e.g. @${handle}) in a List`,
                },
                collaboratorOption('list_collaborator_added', 'List'),
            ],
        },
        {
            key: 'listViews',
            resource: 'listView',
            scope: 'nestedResource',
            title: 'List Views',
            options: [activityOption('list_view_activity', 'List View')],
        },
        {
            key: 'teams',
            resource: 'team',
            scope: 'resource',
            title: 'Teams',
            options: [activityOption('team_activity', 'Team')],
        },
    ];
};

export const notificationWorkflowKeys = (groups: NotificationGroupDefinition[]): string[] =>
    groups.flatMap((group) => group.options.map((option) => option.key));

export const notificationWorkflowDefaultEnabled = (groups: NotificationGroupDefinition[]): Record<string, boolean> =>
    Object.fromEntries(
        groups.flatMap((group) => group.options.map((option) => [option.key, group.scope !== 'nestedResource'])),
    );
